import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import queryString from "query-string";
import { ReactKeycloakProvider as KeycloakProvider } from "@react-keycloak/web";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import keycloak, { getInitOptions } from "src/services/keycloak";
import useGoogleAnalyticsPageview from "src/hooks/useGoogleAnalyticsPageview";
import { ProfileProvider } from "src/providers/profile-provider";
import { TokenProvider } from "src/providers/token-provider";
import { AnalyticsProvider } from "src/providers/analytics-provider";
import TipsProvider from "./providers/tip-provider";

import { Routes } from "./Routes";

interface LocationState {
  from: {
    pathname: string;
    search: string;
    hash: string;
  }
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // ✅ turns retries off in tests to avoid timeout/flakiness
      retry: process.env.NODE_ENV !== "test"
    }
  },
  logger: {
    // eslint-disable-next-line no-console
    log: console.log,
    // eslint-disable-next-line no-console
    warn: console.warn,
    // eslint-disable-next-line no-console
    error: process.env.NODE_ENV === "test" ? () => {} : console.error
  }
});

const App = () => {
  useGoogleAnalyticsPageview();

  const location = useLocation<LocationState | undefined>();
  const responseMode = queryString.parse(location.search).response_mode?.toString();
  const initOptions = useMemo(() => getInitOptions(
    location.pathname,
    responseMode
  ), [location.pathname, responseMode]);

  return (
    <KeycloakProvider
      authClient={keycloak}
      initOptions={initOptions}
    >
      <TokenProvider>
        <ProfileProvider>
          <AnalyticsProvider>
            <QueryClientProvider client={queryClient}>
              <TipsProvider>
                <Routes />
              </TipsProvider>
            </QueryClientProvider>
          </AnalyticsProvider>
        </ProfileProvider>
      </TokenProvider>
    </KeycloakProvider>
  );
};

export type { LocationState };
export default App;
